const colour = (index) => `var(--game-colour-${index})`;

/** Presentation geometry in360×160 SVG units. Positive imbalance means left
 * heavy, so its endpoint lowers. Fixed hit frames are not motion targets. */
export function pebbleBalanceGeometry(imbalance) {
  const angle = imbalance === 0 ? 0 : Math.max(-9, Math.min(9, -imbalance * 1.5));
  const radians = (angle * Math.PI) / 180;
  return {
    angle,
    trays: [67, 293].map((anchor, index) => ({
      side: index + 1,
      anchor,
      x: 180 + (anchor - 180) * Math.cos(radians),
      y: 42 + (anchor - 180) * Math.sin(radians),
    })),
  };
}

function stoneStops(weight) {
  const tint = colour(weight % 6);
  return [
    ['0%', 'var(--game-white)'],
    ['9%', `color-mix(in srgb,${tint} 25%,var(--game-white))`],
    ['32%', `color-mix(in srgb,${tint} 40%,var(--game-paper-muted))`],
    ['68%', `color-mix(in srgb,${tint} 45%,var(--game-night))`],
    ['88%', `color-mix(in srgb,${tint} 15%,var(--game-night))`],
    ['100%', `color-mix(in srgb,${tint} 50%,var(--game-white))`],
  ];
}

/** A polished stone exposes the validated visible weight and current tray only;
 * canonical material helpers are injected so appearance cannot own game policy. */
export function pebbleCellArtwork(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art creative-pebble');
  const fill = kit.material(art, 'radialGradient', stoneStops(cell.weight));
  kit.add(ctx, art, 'ellipse', { cx: 52, cy: 83, rx: 31, ry: 6, fill: 'var(--game-shadow)' });
  kit.path(ctx, art, 'M14 57Q9 34 32 20Q58 10 79 29Q97 47 83 71Q68 88 39 81Q19 77 14 57Z', {
    fill,
    stroke: 'var(--game-glass)',
    'stroke-width': 2,
  });
  kit.path(ctx, art, 'M26 41Q43 21 67 30', {
    stroke: 'var(--game-white)',
    fill: 'none',
    opacity: 0.6,
    'stroke-width': 3,
    'stroke-linecap': 'round',
  });
  kit.path(ctx, art, 'M20 59Q40 45 69 63M24 67Q43 52 76 68', {
    stroke: 'var(--game-glass)',
    fill: 'none',
    'stroke-width': 1,
    opacity: 0.65,
  });
  kit.path(ctx, art, 'M61 23Q47 38 69 54Q77 65 66 79', {
    stroke: 'var(--game-night)',
    fill: 'none',
    'stroke-width': 1.1,
    opacity: 0.25,
  });
  kit.add(
    ctx,
    art,
    'text',
    {
      x: 50,
      y: 60,
      'text-anchor': 'middle',
      'font-size': 28,
      'font-weight': 750,
      fill: 'var(--game-night)',
      stroke: 'var(--game-paper)',
      'stroke-width': 2.5,
      'paint-order': 'stroke fill',
    },
    String(cell.weight),
  );
  kit.add(
    ctx,
    art,
    'text',
    { x: 50, y: 97, 'text-anchor': 'middle', 'font-size': 13, fill: 'var(--game-white)' },
    ['Choose', 'Left', 'Right'][cell.side],
  );
  return art;
}

/** Original scale consumes accepted visible weights/placements and canonical
 * totals; it never solves a partition or assigns a reward. */
export function pebbleSceneArtwork(ctx, art, state, scene, kit) {
  art.setAttribute('viewBox', '0 0 360 160');
  art.setAttribute('class', 'game-scene-art creative-world creative-balance-world');
  const { angle: tilt, trays } = pebbleBalanceGeometry(scene.imbalance);
  art.setAttribute('data-scale-balanced', String(state.completed && scene.imbalance === 0));
  const brass = kit.material(art, 'linearGradient', [
    ['0%', 'var(--game-white)'],
    ['18%', colour(3)],
    ['35%', colour(3)],
    ['43%', 'var(--game-night)'],
    ['51%', colour(3)],
    ['62%', 'var(--game-white)'],
    ['74%', colour(3)],
    ['100%', 'var(--game-night)', 0.9],
  ]);
  const glass = kit.material(art, 'linearGradient', [
    ['0%', 'var(--game-white)', 0.8],
    ['30%', colour(4), 0.3],
    ['65%', colour(4), 0.15],
    ['100%', 'var(--game-white)', 0.6],
  ]);
  kit.add(ctx, art, 'ellipse', {
    cx: 180,
    cy: 148,
    rx: 77,
    ry: 9,
    fill: 'var(--game-night)',
    opacity: 0.5,
  });
  kit.path(ctx, art, 'M121 147Q122 131 147 129L172 115V49H188V115L213 129Q238 131 239 147Z', {
    fill: brass,
    stroke: colour(3),
    'stroke-width': 1,
  });
  kit.path(ctx, art, 'M178 62V116M142 137H218', {
    fill: 'none',
    stroke: 'var(--game-white)',
    opacity: 0.7,
    'stroke-width': 2,
    'stroke-linecap': 'round',
  });
  kit.add(ctx, art, 'ellipse', { cx: 180, cy: 145, rx: 58, ry: 5, fill: brass });
  const beam = kit.add(ctx, art, 'g', {
    'data-scale-angle': tilt,
    transform: `rotate(${tilt} 180 42)`,
  });
  kit.path(ctx, beam, 'M48 42H312', {
    stroke: brass,
    'stroke-width': 10,
    'stroke-linecap': 'round',
  });
  kit.path(ctx, beam, 'M55 39H305', {
    stroke: 'var(--game-white)',
    'stroke-width': 1.5,
    opacity: 0.8,
  });
  kit.circle(ctx, art, 180, 42, 13, { fill: brass, stroke: colour(3), 'stroke-width': 2 });
  kit.circle(ctx, art, 180, 42, 6, {
    fill: colour(4),
    stroke: 'var(--game-white)',
    'stroke-width': 1,
  });
  for (const { side, anchor, x, y } of trays) {
    // Fixed transparent geometry keeps both legal thumb targets stable while the
    // independently anchored visual tray settles to its accepted weight height.
    const frame = kit.add(ctx, art, 'g', { 'data-tray-side': side, 'data-tray-height': y });
    kit.add(ctx, frame, 'rect', {
      x: anchor - 58,
      y: 1,
      width: 116,
      height: 158,
      fill: 'transparent',
    });
    const tray = kit.add(ctx, frame, 'g', { 'data-tray-motion': side });
    kit.path(ctx, tray, `M${x} ${y}L${x - 49} ${y + 60}M${x} ${y}L${x + 49} ${y + 60}`, {
      fill: 'none',
      stroke: brass,
      'stroke-width': 2,
    });
    kit.add(ctx, tray, 'ellipse', {
      cx: x,
      cy: y + 66,
      rx: 52,
      ry: 9,
      fill: 'var(--game-night)',
      opacity: 0.35,
    });
    kit.path(
      ctx,
      tray,
      `M${x - 52} ${y + 60}Q${x - 40} ${y + 80} ${x} ${y + 80}Q${x + 40} ${y + 80} ${x + 52} ${y + 60}Z`,
      { fill: glass, stroke: colour(4), 'stroke-width': 1.5 },
    );
    kit.add(ctx, tray, 'ellipse', {
      cx: x,
      cy: y + 60,
      rx: 52,
      ry: 9,
      fill: glass,
      stroke: 'var(--game-white)',
      'stroke-width': 1.5,
    });
    const placed = state.data.board
      .map((value, index) => (value === side ? index : -1))
      .filter((index) => index >= 0);
    for (const [position, index] of placed.entries()) {
      const weight = state.data.weights[index];
      const px = x + ((position % 3) - 1) * 27,
        py = y + 57 - Math.floor(position / 3) * 20;
      const stoneFill = kit.material(art, 'radialGradient', stoneStops(weight));
      const stone = kit.add(ctx, tray, 'g', {
        'data-placed-pebble': index,
        'data-weight': weight,
      });
      kit.add(ctx, stone, 'ellipse', {
        cx: px,
        cy: py + 7,
        rx: 14,
        ry: 5,
        fill: 'var(--game-night)',
        opacity: 0.5,
      });
      kit.path(
        ctx,
        stone,
        `M${px - 13} ${py + 2}Q${px - 14} ${py - 11} ${px} ${py - 12}Q${px + 16} ${py - 11} ${px + 14} ${py + 2}Q${px} ${py + 14} ${px - 13} ${py + 2}Z`,
        { fill: stoneFill, stroke: 'var(--game-glass)', 'stroke-width': 0.7 },
      );
      kit.path(ctx, stone, `M${px - 8} ${py - 5}Q${px - 2} ${py - 10} ${px + 5} ${py - 7}`, {
        fill: 'none',
        stroke: 'var(--game-white)',
        'stroke-width': 1.2,
        opacity: 0.8,
        'stroke-linecap': 'round',
      });
      kit.add(
        ctx,
        stone,
        'text',
        {
          x: px,
          y: py + 4,
          'text-anchor': 'middle',
          'font-size': 10,
          'font-weight': 800,
          fill: 'var(--game-night)',
          stroke: 'var(--game-paper)',
          'stroke-width': 1.2,
          'paint-order': 'stroke fill',
        },
        String(weight),
      );
    }
    kit.path(ctx, tray, `M${x - 40} ${y + 67}Q${x} ${y + 80} ${x + 40} ${y + 67}`, {
      fill: 'none',
      stroke: 'var(--game-white)',
      'stroke-width': 1.2,
      opacity: 0.65,
    });
    kit.add(
      ctx,
      tray,
      'text',
      {
        x,
        y: y + 96,
        'data-tray-total': side,
        'text-anchor': 'middle',
        'font-size': 13,
        'font-weight': 750,
        fill: 'var(--game-white)',
      },
      `${side === 1 ? 'Left' : 'Right'} ${side === 1 ? scene.left : scene.right}`,
    );
  }
}
